import AuthPanel from '@components/v2/layout/AuthPanel/AuthPanel';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { CircleCheck } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { LoginPageTestIds } from '../../../../utils/testIds/LoginPage.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import useOAuthConsentPanelStyles from './oauthConsentPanel.styles';

const SCOPE_LABEL_MAP: Partial<Record<string, LangEnum>> = {
  email: LangEnum.OAUTH_SCOPE_EMAIL,
  profile: LangEnum.OAUTH_SCOPE_PROFILE,
  mcp: LangEnum.OAUTH_SCOPE_MCP,
  api: LangEnum.OAUTH_SCOPE_API,
};

interface OAuthConsentPanelProps {
  clientName: string;
  scopes: string[];
  isSubmitting: boolean;
  onConsent: () => Promise<void>;
  onDeny: () => void;
}

const OAuthConsentPanel = ({
  clientName,
  scopes,
  isSubmitting,
  onConsent,
  onDeny,
}: OAuthConsentPanelProps) => {
  const { classes } = useOAuthConsentPanelStyles();
  const { translate } = useTranslation();

  return (
    <AuthPanel>
      <div className={classes.root}>
        <Typography variant="h5" align="center">
          {translate(LangEnum.OAUTH_CONSENT_TITLE, { clientName })}
        </Typography>
        {scopes.length > 0 && (
          <div className={classes.scopes}>
            {scopes.map((scope) => (
              <div key={scope} className={classes.scopeRow}>
                <Box className={classes.scopeIcon}>
                  <Icon icon={CircleCheck} size="md" />
                </Box>
                <Typography variant="body2">
                  {SCOPE_LABEL_MAP[scope] ? translate(SCOPE_LABEL_MAP[scope]) : scope}
                </Typography>
              </div>
            ))}
          </div>
        )}
        <div className={classes.actions}>
          <Button
            testId={LoginPageTestIds.OAUTH_CONSENT_DENY_BUTTON}
            variant="text"
            color="secondary"
            size="large"
            fullWidth
            disabled={isSubmitting}
            onClick={onDeny}
          >
            {translate(LangEnum.OAUTH_CONSENT_DENY)}
          </Button>
          <Button
            testId={LoginPageTestIds.OAUTH_CONSENT_ALLOW_BUTTON}
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            loading={isSubmitting}
            onClick={onConsent}
          >
            {translate(LangEnum.OAUTH_CONSENT_ALLOW)}
          </Button>
        </div>
      </div>
    </AuthPanel>
  );
};

export default OAuthConsentPanel;
